import i18nFactory from '@yandex-int/i18n';
import { findIndex, isEmpty, keys } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { getScreenOrientationByScreens } from '../../service/IndoorOutdoorHelpers/IndoorOutdoorHelpers';
import { pluralizeWord } from '../../service/Pluralize/Pluralize';
import {
    CheckBoxChangeData,
    Dictionary,
    DisplayListItem,
    DisplayPoint,
    DisplayRegion,
    FiltersValue,
    IndoorDictionary,
    MapType,
    OutdoorDictionary,
    SelectedScreens
} from '../../typings/displayMap';
import {
    CheckboxDecorator,
    CheckboxDecoratorCheckedItem
} from '../CheckboxDecorator/CheckboxDecorator';
import { DisplayList } from '../DisplayList/DisplayList';
import { DisplayMap } from '../DisplayMap/DisplayMap';
import {
    EasyFilters,
    selectedFilters as easySelectedFilters
} from '../EasyFilters/EasyFilters';
import { SearchInputWithSuggest } from '../SearchInputWithSuggest/SearchInputWithSuggest';

import './DisplayChooser.css';
import * as keyset from './DisplayChooser.i18n';
import { IndoorUtils } from './Indoor/Indoor';
import { OutdoorUtils } from './Outdoor/Outdoor';

const i18n = i18nFactory(keyset);

export interface DisplayChooserState {
    refInited: boolean;
}

export interface DisplayChooserProps {
    mapType: MapType;
    zoomToRegion?: string;
    searchText: string;
    zoomToGroup?: DisplayListItem;
    filteredRegions: DisplayRegion[];
    filteredScreenGroupsByRegion: {
        [id: string]: DisplayListItem[];
    };
    selectedScreens: SelectedScreens;
    filtersValue: FiltersValue;
    selectedFilters: {
        [name: string]: easySelectedFilters;
    };
    scrollToRow?: string | undefined;
    highlightedGroups: string[];
    screensCount: number;
    dictionary: Dictionary;
    showAddressPoint: boolean;
    onAnimationComplete: () => void;
    onSuggestItemSelect: (elementProps: {}) => void;
    onScreensSelect: (data: CheckBoxChangeData) => void;
    onFiltersChange: (filtersState: {
        [name: string]: easySelectedFilters;
        filterChanged: string;
    }) => void;
    onAllGroupsSelect: (
        e: React.KeyboardEvent<HTMLElement>,
        data: CheckboxDecoratorCheckedItem
    ) => void;
    onPlatformClick: (groupId: string[]) => void;
    onBalloonCaptionClick: (groupId: string) => void;
    onAddressGroupClick: (group: DisplayListItem) => void;
    onSearchInput: (searchText: string) => void;
    onMapLoaded: (onMapLoaded: boolean) => void;
    drawPolygonsDrawer?: boolean;
    isNewInterface?: boolean;
}

export class DisplayChooser extends React.Component<
    DisplayChooserProps,
    DisplayChooserState
> {
    state = {
        refInited: false
    };

    private block = 'display-chooser';
    private ref = React.createRef<HTMLDivElement>();

    componentDidMount() {
        this.setState({ refInited: true });
    }

    render() {
        const {
            filteredRegions,
            filteredScreenGroupsByRegion,
            selectedScreens,
            zoomToRegion,
            mapType,
            onPlatformClick,
            onBalloonCaptionClick,
            onScreensSelect,
            zoomToGroup,
            drawPolygonsDrawer,
            showAddressPoint,
            onMapLoaded,
            isNewInterface
        } = this.props;

        return (
            <div className={Bem.cls(this.block)}>
                {this.buildToolBox()}
                <DisplayMap
                    mapType={mapType}
                    zoomToRegion={zoomToRegion}
                    zoomToGroup={zoomToGroup}
                    showAddressPoint={showAddressPoint}
                    regions={filteredRegions}
                    platforms={this.formatToMap(
                        filteredScreenGroupsByRegion,
                        filteredRegions
                    )}
                    selectedScreens={selectedScreens}
                    onPlatformClick={onPlatformClick}
                    onBalloonCaptionClick={onBalloonCaptionClick}
                    onScreensSelect={onScreensSelect}
                    drawPolygonsDrawer={drawPolygonsDrawer}
                    onMapLoaded={onMapLoaded}
                    isNewInterface={isNewInterface}
                />
            </div>
        );
    }

    private buildToolBox = () => {
        const {
            mapType,
            scrollToRow,
            onAnimationComplete,
            onSuggestItemSelect,
            onScreensSelect,
            selectedScreens,
            filtersValue,
            selectedFilters,
            onFiltersChange,
            highlightedGroups,
            dictionary,
            filteredRegions,
            filteredScreenGroupsByRegion,
            onAddressGroupClick,
            searchText,
            onSearchInput
        } = this.props;

        const groupsList = filteredRegions.reduce(
            (platforms: DisplayListItem[], region: DisplayRegion) =>
                platforms.concat(filteredScreenGroupsByRegion[region.id]),
            []
        );

        let scrollToIndex = undefined;

        if (scrollToRow) {
            scrollToIndex = findIndex(
                groupsList,
                (group: DisplayListItem) => group.id === scrollToRow
            );
        }

        return (
            <div className={Bem.cls(this.block, 'toolbox')} ref={this.ref}>
                <SearchInputWithSuggest
                    cls={Bem.cls(this.block, 'search-input')}
                    text={searchText}
                    onChange={onSearchInput}
                    onItemSelect={onSuggestItemSelect}
                    noItemsRender={this.onNoItemsRender}
                />
                <EasyFilters
                    cls={Bem.cls(this.block, 'filters')}
                    filters={this.getFilters(filtersValue, selectedFilters)}
                    onChange={onFiltersChange}
                    anchor={this.ref.current || undefined}
                />
                {this.buildMassCheckbox(groupsList)}
                <DisplayList
                    searchText={searchText}
                    mapType={mapType}
                    scrollToRow={scrollToIndex}
                    onAnimationComplete={onAnimationComplete}
                    onAddressGroupClick={onAddressGroupClick}
                    platforms={groupsList}
                    onScreensSelect={onScreensSelect}
                    selectedScreens={selectedScreens}
                    selectedRegionsCount={filteredRegions.length}
                    highlightedGroups={highlightedGroups}
                    dictionary={dictionary}
                />
            </div>
        );
    };

    private onNoItemsRender = () => {
        return (
            <div className={Bem.cls(this.block, 'empty-msg')}>
                {i18n('nothing-found')}
            </div>
        );
    };

    private buildMassCheckbox = (groupsList: DisplayListItem[]) => {
        const {
            filteredRegions,
            filteredScreenGroupsByRegion,
            selectedScreens,
            onAllGroupsSelect,
            screensCount
        } = this.props;
        const allChecked =
            !isEmpty(groupsList) &&
            filteredRegions.every((region: DisplayRegion) => {
                const selected = selectedScreens[region.id] || [];
                const filtered = filteredScreenGroupsByRegion[region.id] || [];

                return filtered.every((group: DisplayListItem) =>
                    selected.includes(group.id)
                );
            });
        const selectedScreensCount = Object.keys(
            filteredScreenGroupsByRegion
        ).reduce((length: number, regionId: string) => {
            filteredScreenGroupsByRegion[regionId].map(
                (group: DisplayListItem) => {
                    length += group.screens.length;
                }
            );

            return length;
        }, 0);

        return (
            <div className={Bem.cls(this.block, 'mass-checkbox-wrap')}>
                <CheckboxDecorator
                    cls={Bem.cls(this.block, 'mass-checkbox')}
                    theme={'normal'}
                    view={'default'}
                    size={'m'}
                    onChecked={onAllGroupsSelect}
                    checked={allChecked}
                >
                    <div>
                        {screensCount === selectedScreensCount
                            ? pluralizeWord(
                                  [
                                      i18n('one-screens-count', {
                                          selectedCount: selectedScreensCount
                                      }),
                                      i18n('some-screens-count', {
                                          selectedCount: selectedScreensCount
                                      }),
                                      i18n('many-screens-count', {
                                          selectedCount: selectedScreensCount
                                      })
                                  ],
                                  selectedScreensCount
                              )
                            : pluralizeWord(
                                  [
                                      i18n('one-platforms-count', {
                                          selectedCount: selectedScreensCount,
                                          totalCount: screensCount
                                      }),
                                      i18n('some-platforms-count', {
                                          selectedCount: selectedScreensCount,
                                          totalCount: screensCount
                                      }),
                                      i18n('many-platforms-count', {
                                          selectedCount: selectedScreensCount,
                                          totalCount: screensCount
                                      })
                                  ],
                                  selectedScreensCount
                              )}
                    </div>
                </CheckboxDecorator>
            </div>
        );
    };

    private getFilters = (
        filtersValue: FiltersValue,
        selectedFilters: {
            [name: string]: easySelectedFilters;
        }
    ) => {
        const { mapType, dictionary } = this.props;

        switch (mapType) {
            case MapType.indoor:
                return IndoorUtils.getFilters(
                    filtersValue,
                    selectedFilters,
                    dictionary as IndoorDictionary
                );
            case MapType.outdoor:
                return OutdoorUtils.getFilters(
                    filtersValue,
                    selectedFilters,
                    dictionary as OutdoorDictionary
                );
            default:
                return [];
        }
    };

    private formatToMap = (
        groupsByRegion: {
            [id: string]: DisplayPoint[];
        },
        regions: DisplayRegion[]
    ) => {
        const pointByRegion: { [id: string]: DisplayPoint[] } = {};

        regions.forEach((region: DisplayRegion) => {
            pointByRegion[region.id] = this.groupByCoordinates(
                groupsByRegion[region.id]
            );
        }, this);

        return pointByRegion;
    };

    private groupByCoordinates = (groups: DisplayPoint[]): DisplayPoint[] => {
        const coordinates = {};
        const { dictionary, mapType } = this.props;

        groups.forEach((group: DisplayPoint) => {
            const crdId = group.coordinates.join(':');

            if (!coordinates[crdId]) {
                coordinates[crdId] = {
                    id: crdId,
                    coordinates: group.coordinates,
                    regionId: group.regionId,
                    groups: []
                };
            }

            coordinates[crdId].groups.push(group);
        });

        return keys(coordinates).map(crdId => {
            const displayGroup = coordinates[crdId];

            return {
                id: displayGroup.id,
                coordinates: displayGroup.coordinates,
                regionId: displayGroup.regionId,
                screens: displayGroup.groups.map((group: DisplayPoint) => {
                    const deletedScreenWeight = group.screens.reduce(
                        (count, screen) => (screen.isDeleted ? ++count : count),
                        0
                    );

                    return {
                        id: group.id,
                        screenWeight: group.screens.length,
                        deletedScreenWeight: deletedScreenWeight,
                        caption: group.caption,
                        facilityTypeId: group.facilityType,
                        facilityType:
                            group.facilityType &&
                            dictionary.facility[group.facilityType],
                        zoneCategory:
                            group.zoneCategory &&
                            'zone' in dictionary &&
                            dictionary.zone[group.zoneCategory],
                        orientation:
                            mapType === MapType.indoor
                                ? getScreenOrientationByScreens(group.screens)
                                : undefined,
                        operatorName: group.operatorName,
                        transform: group.transform
                    };
                })
            };
        });
    };
}
